import React, { useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { GraduationCapIcon, TrendingUpIcon, UsersIcon, MonitorIcon } from 'lucide-react';
import { DEFAULT_HIGHLIGHTS, EDUCATION_CITATIONS, getDefaultExploreBridge, getExploreBridge } from '../data/exploreBridges';
import { resolveProgramCodeSlug } from '../data/programBridges';
import { useMetaTags } from '../hooks/useMetaTags';
import { buildClicksSearchUrl } from '../utils/clicksSearchUrl';
import { applyUrlPassthrough, pruneEmptyParams } from '../utils/urlPassthrough';
import { BrandedExploreBridge } from '../components/BrandedExploreBridge';
import { getUniversityBySlug } from '../data/universityData';

// Ad-copy bridge template served at /explore/{brand}-{version}
// Icons paired positionally with the outcome stats: earnings, graduates,
// online enrollment.
const HIGHLIGHT_ICONS = [TrendingUpIcon, UsersIcon, MonitorIcon];
// Brand-neutral ad copy used when an /explore/ path matches no template.
const EXPLORE_FALLBACK_SLUG = 'search-v1';
// (e.g. /explore/UAGC-v1). Layout is fixed across every template:
// site nav → hero call to action → short paragraph section → footer.
// The copy, imagery, and outbound destination come from data/exploreBridges.
export function ExploreBridgePage() {
  const {
    bridgeSlug
  } = useParams<{
    bridgeSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to the path segment so statically-registered routes (which
  // have no :bridgeSlug param) resolve the same as dynamic ones.
  const slug = bridgeSlug || location.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop();
  // Previewing the dynamic route directly (path shown as "/explore/:bridgeSlug")
  // has no real slug, so show the first template rather than a dead end.
  const isPlaceholderSlug = !slug || slug.startsWith(':');
  const bridge = isPlaceholderSlug ? getDefaultExploreBridge() : getExploreBridge(slug);
  // /explore/{degreeInterest}-{mProgram} (e.g. /explore/3-101) is shorthand
  // for the named page — redirect so the readable URL is what's shared and
  // reported on. Any query string carries over.
  const codeSlug = isPlaceholderSlug ? undefined : resolveProgramCodeSlug(slug);
  // An /explore/ path that matches nothing falls back to the brand-neutral
  // search page rather than dead-ending the visitor.
  const needsFallback = !bridge && !codeSlug;
  useEffect(() => {
    if (codeSlug) {
      navigate(`/explore/${codeSlug}${location.search}`, {
        replace: true
      });
      return;
    }
    if (needsFallback) {
      navigate(`/explore/${EXPLORE_FALLBACK_SLUG}${location.search}`, {
        replace: true
      });
    }
  }, [codeSlug, needsFallback, navigate, location.search]);
  useMetaTags({
    title: bridge?.metaTitle || 'Explore Online Degrees | FindOnlineDegrees',
    description: bridge?.metaDescription || 'Explore accredited online degree programs from our partner schools.',
    ogImage: bridge?.heroImage,
    // Ad-copy pages shouldn't compete with organic pages in search.
    robots: 'noindex, follow'
  });
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  // Carry any lead data and tracking params on this page's URL through to
  // the destination so the click can be attributed and the user isn't
  // asked for information they already provided.
  const handleCta = () => {
    if (!bridge) return;
    if (bridge.clicksSearch) {
      const url = buildClicksSearchUrl(location.search, bridge.clicksSearch);
      console.log('[Explore Bridge] redirecting to:', url);
      window.location.href = url;
      return;
    }
    if (!bridge.destinationUrl) return;
    const search = location.search.replace(/^\?/, '');
    const isExternal = /^https?:\/\//i.test(bridge.destinationUrl);
    if (isExternal) {
      // A fixed outbound link keeps every value it was built with. Incoming
      // URL params only fill keys the link doesn't already set (publisher and
      // certification keys excluded), then blanks/macros/"unknown" are pruned.
      const url = new URL(bridge.destinationUrl);
      applyUrlPassthrough(url.searchParams, location.search);
      pruneEmptyParams(url.searchParams);
      const finalUrl = url.toString();
      console.log('[Explore Bridge] redirecting to:', finalUrl);
      window.location.href = finalUrl;
      return;
    }
    const separator = bridge.destinationUrl.includes('?') ? '&' : '?';
    const destination = search ? `${bridge.destinationUrl}${separator}${search}` : bridge.destinationUrl;
    if (isExternal) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
    window.scrollTo(0, 0);
  };
  // Branded ad copy renders the advertiser-styled layout instead.
  const university = bridge?.universitySlug ? getUniversityBySlug(bridge.universitySlug) : undefined;
  if (bridge && bridge.layout === 'branded' && university) {
    return <BrandedExploreBridge bridge={bridge} university={university} onCta={handleCta} data-id="element-1774" data-mp="14smf0qw9deuk" />;
  }
  // Numeric shorthand and unmatched slugs both redirect in the effect above;
  // render nothing for the frame in between rather than flashing a dead end.
  if (!bridge) return null;
  const highlights = bridge.highlights ?? DEFAULT_HIGHLIGHTS;
  // Side-fade hero is the house style for these pages; an entry can opt out.
  const sideFade = bridge.heroSideFade !== false;
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1775" data-mp="zvci6q6ji0wp">
      <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1776" data-mp="v32vrk4mviz6" />
      <main className="flex-grow" data-id="element-1777" data-mp="v1i4c3dm8g5j">
        {/* Hero + call to action */}
        <section className={`relative border-b-4 border-[#e8594f] ${sideFade ? 'bg-[#111827]' : ''}`} data-id="element-1778" data-mp="7bxi9bm612s1">
          <img src={bridge.heroImage} alt={bridge.heroImageAlt}
        // Anchored near the top of the frame: these are photographs of
        // people, and a centered crop on a wide short hero cuts their
        // faces off. On a side-fade hero the subject is pulled left so
        // the fade lands on empty background rather than across a face.
        className={`absolute inset-0 w-full h-full object-cover ${sideFade ? 'object-[22%_28%] lg:w-4/5' : 'object-[50%_20%]'}`} data-id="element-1779" data-mp="3ac65shggsiw" />
          {sideFade ?
        // Image on the left, solid panel on the right, fading right to
        // left across the middle of the hero.
        <div className="absolute inset-0 bg-gray-900/45 lg:bg-[linear-gradient(to_left,#111827_0%,#111827_34%,rgba(17,24,39,0.7)_55%,rgba(17,24,39,0.2)_80%,rgba(17,24,39,0.08)_100%)]" aria-hidden="true" data-id="element-1780" data-mp="1tibc316368ba" /> : <div className={`absolute inset-0 ${bridge.heroHeadline ? 'bg-gray-900/55 lg:bg-gray-900/45' : 'bg-gray-900/40 lg:bg-gray-900/20'}`} aria-hidden="true" data-id="element-1781" data-mp="39t5kyrcqsm06" />}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1782" data-mp="t8tvgb4l19mf">
            <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center" data-id="element-1783" data-mp="ofr140rzdw56">
              {bridge.heroHeadline ?
            // Headline leads on every viewport: stacked above the call to
            // action on mobile, beside it on desktop.
            <h1 className="mb-8 lg:mb-0 text-center lg:text-left text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-1784" data-mp="ahaqvzuwtnia">
                  {bridge.heroHeadline}
                </h1> : <div className="hidden lg:block" aria-hidden="true" data-id="element-1785" data-mp="kxpsgx1c7di" />}
              <div className="w-full bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1786" data-mp="1zvfo2s58iv5z">
                {bridge.heroHeadline ? <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1787" data-mp="13sbhp61duxt4">
                    {bridge.cardHeadline}
                  </h2> : <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1788" data-mp="18sz8bqlp9cmt">
                    {bridge.cardHeadline}
                  </h1>}
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1789" data-mp="yzidcj9ttrhk">
                  {bridge.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1790" data-mp="q0n8pbmfkpgd">
                  {bridge.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* National education outcomes — compact band directly under the hero */}
        <section className="bg-gray-50 border-b border-gray-200" aria-label="Education outcomes" data-id="element-1791" data-mp="3uzki2bnsni0e">
          <dl className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-gray-200" data-id="element-1792" data-mp="3q198m21ev9ip">
            {highlights.map((item, i) => {
            const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
            return <div key={item.label} className="flex gap-4 sm:px-6 sm:first:pl-0 sm:last:pr-0" data-id="element-1793" data-mp="hibe5y3tbnxs" data-mpi={i}>
                  <Icon className="mt-1 h-5 w-5 flex-shrink-0 text-[#e8594f]" aria-hidden="true" data-id="element-1794" data-mp="rhsnqqm6qlz1" />
                  <div data-id="element-1795" data-mp="mj8wcaotohza">
                    <dt className="text-xl font-extrabold text-gray-900 tracking-tight leading-none" data-id="element-1796" data-mp="mgwio9q3kts1">
                      {item.stat}{' '}
                      <span className="text-sm font-semibold text-gray-500" data-id="element-1797" data-mp="2oavt6sveb73o">
                        {item.label}
                      </span>
                      {item.note && <sup className="ml-0.5 text-[10px] font-semibold text-gray-400" data-id="element-1798" data-mp="197h018lpgoqs">
                          [{item.note}]
                        </sup>}
                    </dt>
                    <dd className="mt-2 text-sm text-gray-600 leading-relaxed" data-id="element-1799" data-mp="1070ttrdkv3i8">
                      {item.body}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Short paragraph section */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-1800" data-mp="1n6qdi0ij14r2">
          <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12 lg:items-start" data-id="element-1801" data-mp="1n4m08ryqmte4">
            <div data-id="element-1802" data-mp="1j3ah95add6ew">
              <span className="mx-auto lg:mx-0 mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8594f]/10" aria-hidden="true" data-id="element-1803" data-mp="2fn9hfwam5o9x">
                <GraduationCapIcon className="h-6 w-6 text-[#e8594f]" data-id="element-1804" data-mp="2auq1wtsd8j2h" />
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center lg:text-left" data-id="element-1805" data-mp="3tldp745elz5f">
                {bridge.sectionHeading}
              </h2>
              <div className="mt-8 space-y-5" data-id="element-1806" data-mp="6dtl9f4v0g17">
                {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-lg text-gray-600 leading-relaxed" data-id="element-1807" data-mp="beh9xu5m8ks9">
                    {paragraph}
                  </p>)}
              </div>
            </div>

            <img src="https://cdn.magicpatterns.com/patterns/generated-images/c770ed7b-e4d2-4980-b486-3ba0bc2b040d.jpg" alt="Graduate in cap and gown holding her young daughter after her commencement ceremony" className="mt-10 lg:mt-0 w-full max-w-sm mx-auto lg:max-w-none rounded-2xl object-cover aspect-[4/5]" loading="lazy" data-id="element-1808" data-mp="q22eztm1wu2t" />
          </div>

          {/* Related pages — the same program at other levels, and sibling
              programs in the same field. Gives a visitor who is close but not
              committed somewhere to go other than back. */}
          {bridge.relatedGroups?.length ? <div className="mt-14 grid gap-10 sm:grid-cols-2" data-id="element-1809" data-mp="354l0rsasyn3v">
              {bridge.relatedGroups.map(group => <section key={group.heading} data-id="element-1810" data-mp="3ivgcrdgllkbo">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500" data-id="element-1811" data-mp="3iu6cchm9w01v">
                    {group.heading}
                  </h3>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1812" data-mp="3sup561vteqvx">
                    {group.items.map(item => <li key={item.to} data-id="element-1813" data-mp="3nut0e0famvfb">
                        <Link to={item.to} className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-[#e8594f] hover:text-[#e8594f] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e8594f] focus-visible:ring-offset-2" data-id="element-1814" data-mp="3nvvs2akfx1wa">
                          {item.label}
                        </Link>
                      </li>)}
                  </ul>
                </section>)}
            </div> : null}

          <div className="mt-14 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between rounded-2xl bg-gray-50 border border-gray-200 p-6 sm:p-8" data-id="element-1815" data-mp="45xunx5uk6v0">
            <p className="text-lg font-semibold text-gray-900 max-w-md" data-id="element-1816" data-mp="3rekzmmxfh8sv">
              {bridge.closingLine || bridge.cardSubhead}
            </p>
            <button onClick={handleCta} className="w-full sm:w-auto flex-shrink-0 px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1817" data-mp="3hekecvg00im1">
              {bridge.ctaLabel}
            </button>
          </div>

          {bridge.disclosure && <p className="mt-10 text-xs text-gray-400 leading-relaxed" data-id="element-1818" data-mp="37q1yejck4ipw">
              {bridge.disclosure}
            </p>}
        </section>

        {/* Citations for the national statistics above */}
        <section className="border-t border-gray-200 bg-white" aria-label="Citations" data-id="element-1819" data-mp="2xq1disz1hujl">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1820" data-mp="2st14qaat2dos">
            <h2 className="text-sm font-bold text-gray-600 mb-3" data-id="element-1821" data-mp="2o07ytj3lozb4">Citations</h2>
            <ol className="space-y-1.5" data-id="element-1822" data-mp="2jymred9u5d8b">
              {EDUCATION_CITATIONS.links.map((href, i) => <li key={href} className="text-xs text-gray-500" data-id="element-1823" data-mp="2f14hoknzutz9" data-mpi={i}>
                  <span className="mr-1 text-gray-400" data-id="element-1824" data-mp="2a18lh7ljg6u9">[{i + 1}]</span>
                  <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="underline break-all hover:text-gray-700" data-id="element-1825" data-mp="2a0gnxr8drz1c">
                    {href}
                  </a>
                </li>)}
            </ol>
            <div className="mt-5 space-y-1" data-id="element-1826" data-mp="24wo27seffevz">
              {EDUCATION_CITATIONS.notes.map(note => <p key={note} className="text-xs text-gray-500 leading-relaxed" data-id="element-1827" data-mp="29w0yt4v3o0br">
                  {note}
                </p>)}
            </div>
          </div>
        </section>
      </main>
      <Footer onNavigate={handleNavigate} data-id="element-1828" data-mp="1h6ajwtln8uel" />
    </div>;
}